import type { MarketFilter, MarketSubCategory } from '../../services/interfaces/market.types';
import type { BettingLine } from '../../components/MarketCard/types';
import { useNormalizedMarketsStore, type SubCategory } from '../normalizedMarkets.store';
import { useEntitiesStore, type NormalizedTeam } from '../entities.store';

export type NormalizedMarketEvent = {
  marketGroupId: string;
  eventStart: number;
  homeTeam: NormalizedTeam;
  awayTeam: NormalizedTeam;
  homeTeamLine: BettingLine;
  awayTeamLine: BettingLine;
};

export type NormalizedSection = {
  title: string;
  subCategory: MarketSubCategory;
  events: NormalizedMarketEvent[];
};

export type SectionWithIds = {
  title: string;
  subCategory: MarketSubCategory;
  marketGroupIds: string[];
};

export const useDiscoverSections = () =>
  useNormalizedMarketsStore((state) => state.discoverySections);

export const useSubCategories = () => useNormalizedMarketsStore((state) => state.subCategories);

const EMPTY_FILTERS: MarketFilter[] = [];

export const selectSubCategoryFilters = (
  subCategories: SubCategory[],
  subCategory: string | undefined
): MarketFilter[] =>
  subCategories.find((candidate) => candidate.id === subCategory)?.filters ?? EMPTY_FILTERS;

export const useSubCategoryFilters = (subCategory: string | undefined): MarketFilter[] =>
  useNormalizedMarketsStore((state) => selectSubCategoryFilters(state.subCategories, subCategory));

export const useDiscoverSectionsByCategory = (subCategory?: MarketSubCategory) =>
  useNormalizedMarketsStore((state) => {
    if (!subCategory) return state.discoverySections;

    return state.discoverySections
      .map((section) => ({
        ...section,
        market_groups: section.market_groups.filter((group) => group.sub_category === subCategory),
      }))
      .filter((section) => section.market_groups.length > 0);
  });

export const useSectionIds = (subCategory?: MarketSubCategory): SectionWithIds[] =>
  useNormalizedMarketsStore(
    (state) => {
      const filteredSections = subCategory
        ? state.discoverySections.filter((section) => section.sub_category === subCategory)
        : state.discoverySections;

      return filteredSections.map((section) => ({
        title: section.title,
        subCategory: section.sub_category,
        marketGroupIds: section.market_groups.map((mg) => mg.id),
      }));
    },
    (a, b) => {
      if (a.length !== b.length) return false;
      return a.every((sectionA, i) => {
        const sectionB = b[i];
        return (
          sectionA.title === sectionB.title &&
          sectionA.subCategory === sectionB.subCategory &&
          sectionA.marketGroupIds.length === sectionB.marketGroupIds.length &&
          sectionA.marketGroupIds.every((id, j) => id === sectionB.marketGroupIds[j])
        );
      });
    }
  );

export const useSectionsWithEvents = (subCategory?: MarketSubCategory): NormalizedSection[] =>
  useNormalizedMarketsStore((state) => {
    const entitiesState = useEntitiesStore.getState();

    const filteredSections = subCategory
      ? state.discoverySections.filter((section) => section.sub_category === subCategory)
      : state.discoverySections;

    return filteredSections
      .map((section) => {
        const events = section.market_groups
          .map((marketGroup) => {
            const normalizedMG = state.normalizedMarketGroupsById.get(marketGroup.id);
            if (!normalizedMG) return null;

            const homeTeam = entitiesState.normalizedTeamsByEventAndType.get(
              `${normalizedMG.eventId}:home`
            );
            const awayTeam = entitiesState.normalizedTeamsByEventAndType.get(
              `${normalizedMG.eventId}:away`
            );

            if (!homeTeam || !awayTeam) return null;

            return {
              marketGroupId: marketGroup.id,
              eventStart: normalizedMG.eventStart,
              homeTeam,
              awayTeam,
              homeTeamLine: normalizedMG.homeTeamLine,
              awayTeamLine: normalizedMG.awayTeamLine,
            };
          })
          .filter((event): event is NormalizedMarketEvent => event !== null);

        return {
          title: section.title,
          subCategory: section.sub_category,
          events,
        };
      })
      .filter((section) => section.events.length > 0);
  });
